/**
 * User session data returned from /api/session endpoint
 */
export interface UserSession {
  _id: string;
  email: string;
  username: string;
  firstname: string;
  lastname: string;
  name?: string;
  language: 'en' | 'fr';
  organisation: string;
  organisations: string[];
  organisation_data?: OrganisationData[];
  admin: AdminLevel;
  usergroups: string[];
  sensorgroups: string[];
  site?: string;
  location?: string;
  tags: string[];
  configtags: string[];
  permission_strings: string[];
  /** PostgreSQL roles from user_roles table. Always present (empty array if unavailable). */
  postgres_roles: string[];
  /** EOS team memberships (from team_members + teams). Used for team-scoped V2 access. Empty if unavailable. */
  teams?: Array<{ id: string; name: string; role?: string | null }>;
  /** PG users.employment_type — drives the "internal staff" gate in v4. */
  employment_type?: 'employee' | 'contractor' | 'platform' | 'service' | null;
  /** PG users.employment_end_date. */
  employment_end_date?: string | null;
  /** PG UUID of the user. The new v4 canonical identifier. */
  pg_id?: string;
  /** GCS public URL of the user's profile photo. Source of truth: PG
   *  users.profile_photo_url. Surfaced on the V4 session blob so every
   *  device renders the avatar without depending on localStorage. */
  profile_photo_url?: string | null;
  uipreferences?: UIPreference[];
  login_ttl_hrs?: number;
  ai_investigation_enabled?: boolean;
  status: boolean;
  /**
   * Set when the session is currently a "View as user" impersonation by a
   * super-admin. Identifies the original super-admin (the impersonator),
   * not the active (target) identity. Null when not impersonating.
   */
  impersonatedBy?: ImpersonatorRef | null;
  /** Unix ms timestamp at which the impersonation started (60-min TTL). */
  impersonationStartedAt?: number | null;
}

export interface ImpersonatorRef {
  _id: string;
  firstname: string;
  lastname: string;
  email: string;
}

export interface OrganisationData {
  organisation: string;
  admin: AdminLevel;
  usergroups: string[];
  sensorgroups: string[];
  site?: string;
  location?: string;
  tags: string[];
  configtags: string[];
  deleted: boolean;
}

export interface UIPreference {
  key: string;
  value: unknown;
}

export type AdminLevel =
  | 'full admin'
  | 'group admin'
  | 'standard user'
  | 'auditor'
  | 'guest';

/**
 * Check if user has full admin privileges (PostgreSQL role)
 * Includes both 'full_admin' and 'director' roles
 */
export function isFullAdmin(user: UserSession | null): boolean {
  return (user?.postgres_roles?.includes('full_admin') || user?.postgres_roles?.includes('director')) ?? false;
}

/**
 * Check if user has at least group admin privileges
 * @deprecated Use hasAnyRole() with specific roles instead
 */
export function isGroupAdminOrAbove(user: UserSession | null): boolean {
  if (!user?.postgres_roles) return false;
  // Any of the 4 PostgreSQL roles is considered "group admin or above"
  return user.postgres_roles.length > 0;
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(
  user: UserSession | null,
  permission: string
): boolean {
  if (!user?.permission_strings) return false;
  return user.permission_strings.includes(permission);
}

/**
 * Check if user has a specific PostgreSQL role
 */
export function hasRole(user: UserSession | null, role: string): boolean {
  if (!user) return false;
  return user.postgres_roles.includes(role);
}

/**
 * Check if user has any of the specified PostgreSQL roles
 */
export function hasAnyRole(user: UserSession | null, roles: string[]): boolean {
  if (!user || !roles.length) return false;
  return roles.some((role) => user.postgres_roles.includes(role));
}
